import { useEffect, useState } from "react";
import { ClipboardList, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

type Classroom = {
  id: number;
  name: string;
  description?: string | null;
  teacher_name?: string | null;
};

export function ClassroomCard({
  id,
  onClick,
  name: nameProp,
  teacher_name: teacherNameProp,
}: {
  id: number;
  onClick: () => void;
  name?: string;
  teacher_name?: string | null;
}) {
  const [classroom, setClassroom] = useState<Classroom | null>(
    nameProp !== undefined ? { id, name: nameProp, teacher_name: teacherNameProp ?? null } : null,
  );
  const [loading, setLoading] = useState(nameProp === undefined);

  useEffect(() => {
    if (nameProp !== undefined) return;
    const load = async () => {
      try {
        const r = await fetch(`/api/internal/admin/classrooms/${id}`);
        if (!r.ok) throw new Error(`${r.status}`);
        const data: Classroom = await r.json();
        setClassroom(data);
      } catch {
        setClassroom({ id, name: `Classroom ${id}` });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, nameProp]);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border bg-card shadow-sm px-5 py-4 flex items-center gap-4 hover:bg-accent/50 transition-colors group"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-lime-600/10 text-lime-700">
        <ClipboardList className="size-5" />
      </div>
      <div className="flex-1 min-w-0">
        {loading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          <p className="font-medium text-sm text-foreground truncate">
            {classroom?.name}
          </p>
        )}
        {loading ? (
          <Skeleton className="h-3 w-24 mt-1.5" />
        ) : (
          <>
            {classroom?.teacher_name && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Teacher: {classroom.teacher_name}
              </p>
            )}
            {classroom?.description && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Description: {classroom.description}
              </p>
            )}
            {!classroom?.teacher_name && !classroom?.description && (
              <p className="text-xs text-muted-foreground mt-0.5">ID: {id}</p>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Badge variant="secondary" className="text-xs">
          ID: {id}
        </Badge>
        <ChevronRight className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>
    </button>
  );
}
